import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { Eyebrow } from '@/components/landing/eyebrow'
import { setBallDirector } from '@/lib/ball-director'
import { clamp, lerp, prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { SESSIONS } from '@/constants/event'

/**
 * The schedule. The page's 3D ball lands on each session as it crosses the middle of the screen,
 * hopping from row to row while the list scrolls under it.
 */
export const WeekSection: FC = (): ReactNode => {
    const listRef = useRef<HTMLOListElement>(null)
    const rowRefs = useRef<Array<HTMLLIElement | null>>([])
    const spotRefs = useRef<Array<HTMLSpanElement | null>>([])

    useEffect(() => {
        const reduced = prefersReducedMotion()
        let activeRow = -1

        const highlight = (index: number): void => {
            if (index === activeRow) return
            activeRow = index
            rowRefs.current.forEach((row, rowIndex) => row?.toggleAttribute('data-active', rowIndex === index))
        }

        return setBallDirector('week', 2, ({ width, height }) => {
            const list = listRef.current
            if (!list) return null

            const listRect = list.getBoundingClientRect()
            if (listRect.bottom < -60 || listRect.top > height + 60) {
                highlight(-1)
                return null
            }

            const spots = spotRefs.current
                .filter((spot): spot is HTMLSpanElement => Boolean(spot))
                .map((spot) => {
                    const rect = spot.getBoundingClientRect()
                    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
                })

            if (!spots.length) return null

            const radius = clamp(width * 0.016, 14, 22)
            const landing = height * 0.56
            const first = spots[0]
            const last = spots[spots.length - 1]

            let x = first.x
            let y = first.y
            let squash = 0
            let current = 0

            if (first.y >= landing) {
                current = 0
            }
            else if (last.y <= landing) {
                x = last.x
                y = last.y
                current = spots.length - 1
            }
            else {
                let index = 0
                while (index < spots.length - 2 && spots[index + 1].y <= landing) index++

                const from = spots[index]
                const to = spots[index + 1]
                const t = clamp((landing - from.y) / Math.max(to.y - from.y, 1))
                const hop = reduced ? 0 : Math.sin(Math.PI * t) * (to.y - from.y) * 0.6

                x = lerp(from.x, to.x, t)
                y = landing - hop
                squash = reduced ? 0 : Math.max(0, 1 - Math.min(t, 1 - t) * 9)
                current = t < 0.5 ? index : index + 1
            }

            highlight(current)

            const squashY = 1 - 0.18 * squash

            return {
                x,
                y: y - radius * squashY,
                radius,
                squashX: 1 + 0.18 * squash,
                squashY,
                spin: 1.4
            }
        })
    }, [])

    return (
        <section
            id='the-week'
            aria-labelledby='week-title'
            className='relative overflow-hidden bg-court text-cream'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute inset-y-0 left-[8%] w-px bg-cream/10' />
                <div className='absolute inset-y-0 right-[8%] w-px bg-cream/10' />
                <div className='absolute inset-y-0 left-1/2 hidden w-px bg-cream/[0.07] lg:block' />
            </div>
            <div aria-hidden='true' className='grain-overlay absolute inset-0' />

            <div className='relative mx-auto grid max-w-[1440px] gap-12 px-5 py-24 md:px-10 md:py-32 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:sticky lg:top-32 lg:col-span-4 lg:self-start'>
                    <Eyebrow className='text-lime'>The week</Eyebrow>

                    <h2 id='week-title' className='mt-5 font-heading text-[clamp(3.2rem,7vw,6.5rem)] font-black uppercase leading-[0.86]'>
                        Four days,
                        <span className='serif-soft block font-serif font-normal normal-case italic text-lime'>one draw.</span>
                    </h2>

                    <p className='mt-6 max-w-sm text-base leading-relaxed text-cream/80'>
                        Day sessions start at 8:00 AM on all eighteen courts. Night sessions belong to Center Court, under the lights, from 7:00 PM.
                    </p>

                    <p className='mt-8 inline-flex items-center gap-3 rounded-full bg-ink/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-cream/85'>
                        <span aria-hidden='true' className='size-2 rounded-full bg-lime' />
                        Thursday 8 to Sunday 11 April
                    </p>
                </div>

                <ol ref={listRef} className='border-y border-cream/15 lg:col-span-8'>
                    {SESSIONS.map((session, index) => (
                        <li
                            key={`${session.day}-${session.slot}`}
                            ref={(element) => {
                                rowRefs.current[index] = element
                            }}
                            className={cn(
                                'group grid grid-cols-[3.25rem_2.75rem_1fr] items-center gap-x-3 py-6 md:grid-cols-[5.5rem_4.5rem_1fr_auto] md:gap-x-5 md:py-8',
                                index > 0 && 'border-t border-cream/15'
                            )}
                        >
                            <div className='leading-none'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/65'>
                                    {session.day}
                                </p>
                                <p className='mt-1 font-heading text-4xl font-black md:text-5xl'>
                                    {session.date}
                                </p>
                            </div>

                            <div className='relative h-full'>
                                <span
                                    ref={(element) => {
                                        spotRefs.current[index] = element
                                    }}
                                    aria-hidden='true'
                                    className='absolute left-1/2 top-1/2 block size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cream/30 transition-colors group-data-[active]:bg-lime'
                                />
                            </div>

                            <div className='min-w-0'>
                                <p className='font-mono text-[10px] uppercase tracking-[0.2em] text-lime/90'>
                                    {session.slot}
                                </p>
                                <h3 className='mt-1 font-heading text-2xl font-extrabold uppercase leading-[0.95] transition-colors group-data-[active]:text-lime md:text-4xl'>
                                    {session.title}
                                </h3>
                                <p className='mt-1.5 max-w-lg text-sm leading-relaxed text-cream/75'>
                                    {session.description}
                                </p>
                                <p className='mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60 md:hidden'>
                                    {session.where}
                                </p>
                            </div>

                            <p className='hidden text-right font-mono text-[11px] uppercase tracking-[0.16em] text-cream/65 md:block'>
                                {session.where}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
